import { gql, useMutation } from '@apollo/client'
import features from '@nobia/zeus-components/lib/features'
import settings from '@nobia/zeus-components/lib/settings'
import styled, { rem } from '@nobia/zeus-components/lib/styled'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { CustomerFragment } from '../../graphql/CustomerFragments'
import { margins } from '../../styles/margins'
import { gtmEvent } from '../../utils/tracking'
import { useTranslation } from '../../utils/useTranslation'
import { useAccountContext } from '../AccountContext'
import { OutlineButton } from '../button/Button'
import { Loader } from '../loader/Loader'
import { useUser } from '../user-provider/UserProvider'
import { H1 } from './common'
import EditUserDetails from './EditUserDetails'
import UserDesigner from './UserDesigner'
import UserDetails from './UserDetails'
import CustomerCare from './CustomerCare'
import Newsletter from './Newsletter'
import { UserNotificationSettings } from './UserNotificationSettings'
import UserStore from './UserStore'

const SectionWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  max-width: ${rem(900)};
  margin: 0 auto;
`

const WideWrapper = styled.div`
  margin: 0 auto ${rem(65)} auto;
  padding: ${rem(15)};
  ${(p) => p.theme.media.min.md`
    padding: ${rem(50)};
  `}
`

const Section = styled.div`
  grid-column: ${(p) => p.col || 1};
  padding-top: ${rem(margins.m)};
  @media all and (-ms-high-contrast: none) {
    -ms-grid-column: ${(p) => p.col || 1};
    -ms-grid-row: ${(p) => p.row};
  }
  ${(p) =>
    p.noSideMargin &&
    `
    margin-right: 0;
    margin-left: 0;
  `}
  ${(p) => p.theme.media.min.md`
    margin-right: ${rem(10)};
    `}
  ${(p) =>
    p.spanThreeRows &&
    `
    grid-row: 1 / span 3;
    @media all and (-ms-high-contrast: none) {
      -ms-grid-row: 1;
      -ms-grid-row-span: 3;
      margin-right: 20px;
    }
  `}
`
const ResponsiveWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  ${(p) =>
    !p.fullWidth &&
    p.theme.media.min.md`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: ${rem(20)};
  `}
  @media all and (-ms-high-contrast: none) and (min-width: 900px) {
    display: -ms-grid;
    -ms-grid-columns: 1fr 1fr;
  }

  ${(p) =>
    p.isMagnet && p.theme.media.min.md`
    display: block;
    column-count: 2;

    & > div {
      break-inside: avoid;
    }
  `}
`

const UpdateCustomerMutation = gql`
  ${CustomerFragment}
  mutation UpdateCustomerProfile($input: CustomerProfileInput) {
    customer {
      updateProfile(input: $input) {
        ...Customer
      }
    }
  }
`

const UserSettings = forwardRef((props, ref) => {
  const { t } = useTranslation('app.pages.user-settings')
  const { store, staffMember, clearStore } = useAccountContext()
  const { customer, loading } = useUser()
  const { __typename, ...profile } = customer?.profile || {}
  const isMagnet = settings.brand.includes('magnet')

  const [updateCustomer] = useMutation(UpdateCustomerMutation)
  const [editDetails, setEditDetails] = useState(false)
  const [detailsAreCorrect, setDetailsAreCorrect] = useState(
    JSON.parse(localStorage.getItem('details-are-correct') || 'false')
  )
  const messagingEnabled = useMemo(
    () => customer?.messagingEnabled || false,
    [customer]
  )

  useEffect(() => {
    if (!window) return
    window.scrollTo(0, 0)
  }, [])

  const onLogout = useCallback(() => {
    gtmEvent('Log out')
    if (features.multiStore === true) {
      clearStore()
    }
    localStorage.removeItem('orderNotificationState')
    window.location.href = settings.logout.path
  }, [clearStore])

  const onUpdateCustomer = useCallback(
    async (user) => {
      await updateCustomer({ variables: { input: { ...profile, ...user } } })
    },
    [updateCustomer, profile]
  )

  const onClose = useCallback(() => {
    setEditDetails(false)
    setDetailsAreCorrect(true)
    localStorage.setItem('details-are-correct', true)
  }, [])

  const onOpen = useCallback(() => {
    setEditDetails(true)
    gtmEvent('Edit my details')
  }, [])

  const onSave = useCallback(
    async (newData) => {
      gtmEvent('Save my details')
      await onUpdateCustomer(newData)
      onClose()
    },
    [onClose, onUpdateCustomer]
  )

  const onDetailsCorrect = useCallback(
    (correct) => {
      gtmEvent('Confirm details', correct ? 'Yes' : 'No')
      if (!correct) {
        onOpen()
      } else {
        onClose()
      }
    },
    [onClose, onOpen]
  )

  if (loading) {
    return <Loader />
  }

  return (
    <WideWrapper>
      <SectionWrapper>
        {editDetails && (
          <EditUserDetails
            onClose={() => setEditDetails(false)}
            onSubmit={onSave}
            user={{ ...profile }}
            ref={ref}
          />
        )}
        {!editDetails && (
          <>
            <H1>{t('heading')}</H1>
            <ResponsiveWrapper fullWidth={!store && !staffMember && !isMagnet} isMagnet={isMagnet}>
              <Section spanThreeRows>
                <UserDetails
                  {...profile}
                  customerNumber={customer?.sapUserId}
                  detailsAreCorrect={detailsAreCorrect}
                  onEditCustomer={onOpen}
                  onDetailsCorrect={onDetailsCorrect}
                />
              </Section>
              {store && (
                <Section col={2} row={1}>
                  <UserStore {...store} />
                </Section>
              )}
              {staffMember && (
                <Section col={2} row={2}>
                  <UserDesigner {...staffMember} />
                </Section>
              )}
              {isMagnet && (
                <>
                  <Section col={2} row={1}>
                    <CustomerCare />
                  </Section>
                  <Section col={2} row={1}>
                    <Newsletter profile={profile} />
                  </Section>
                </>
              )}
              {messagingEnabled && (
                <Section col={2} row={1}>
                  <UserNotificationSettings settings={customer?.settings} />
                </Section>
              )}
              <Section>
                <OutlineButton
                  id="mp-account-details-logout"
                  fullWidth
                  onClick={onLogout}
                >
                  {t('logout')}
                </OutlineButton>
              </Section>
            </ResponsiveWrapper>
          </>
        )}
      </SectionWrapper>
    </WideWrapper>
  )
})

export default UserSettings
